import { t } from './i18n';
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

function isWrite(method: string | undefined): boolean {
  return Boolean(method && method !== 'GET' && method !== 'HEAD');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const write = isWrite(init?.method);
  const timeout = AbortSignal.timeout(25_000);
  const signal = init?.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  try {
    const res = await fetch(`/api${path}`, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      ...init,
      signal,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        error?: string | { message?: string };
      } | null;
      const message = typeof body?.error === 'string' ? body.error : body?.error?.message;
      throw new ApiError(t(message ?? 'Сервер недоступен'), res.status, body);
    }
    return (await res.json()) as T;
  } catch (error) {
    if (timeout.aborted && !init?.signal?.aborted) {
      throw new ApiError(
        write
          ? 'Ответ не получен за 25 секунд. Неизвестно, сохранилась ли запись.'
          : 'Источник не ответил за 25 секунд. Попробуйте обновить страницу.',
        504,
        { outcome: write ? 'unknown' : 'timeout' },
      );
    }
    throw error;
  }
}

export const api = {
  get: <T>(path: string, options?: { signal?: AbortSignal }) => request<T>(path, options),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ── Типы, разделяемые с сервером ──────────────────────────────────────────

export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface Task {
  updated_at?: string;
  created_at?: string;
  id: string;
  project_id: string;
  parent_id: string | null;
  level: 0 | 1 | 2;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string | null;
  assignee_id: string | null;
  recurrence_rule: string | null;
  position: number;
  project_title?: string;
  project_color?: string;
  assignee_name?: string | null;
  assignee_color?: string | null;
  child_count?: number;
  child_done?: number;
}

export interface Project {
  id: string;
  title: string;
  description: string | null;
  color: string;
  icon: string | null;
  position: number;
  archived_at: string | null;
  open_tasks: number;
  total_tasks: number;
}

/** Короткий срез активного проекта для Today. Не является вторым списком задач. */
export interface ProjectSummary {
  id: string;
  title: string;
  color: string;
  total_tasks: number;
  done_tasks: number;
  open_tasks: number;
  next_task_id: string | null;
  next_task_title: string | null;
  next_task_due_date: string | null;
  next_task_priority: TaskPriority | null;
}

export type DecisionStatus = 'active' | 'superseded';

export interface Decision {
  id: string;
  title: string;
  rationale: string | null;
  impact: string | null;
  status: DecisionStatus;
  created_by: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

export type WaitingStatus = 'open' | 'received' | 'cancelled';

export interface WaitingItem {
  id: string;
  title: string;
  waiting_for: string;
  details: string | null;
  follow_up_date: string | null;
  status: WaitingStatus;
  created_by: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

export interface HouseholdMember {
  id: string;
  name: string;
  color: string;
}

/** Ответ на изменение задачи: сама задача и порождённый повтор, если он был. */
export interface TaskMutation {
  task: Task;
  spawned: Task | null;
}

export interface NoteStub {
  id: string;
  title: string;
  updated_at: string;
}

export interface Dashboard {
  today: string;
  dueToday: Task[];
  overdue: Task[];
  upcoming: Task[];
  inboxOpen: number;
  inboxItems: Task[];
  waitingItems: WaitingItem[];
  waitingOpen: number;
  /** Открытые ожидания, которые пора пинговать сегодня или уже просрочены. */
  waitingFollowUp: number;
  activeDecisions: Decision[];
  activeDecisionsCount: number;
  /** Активные решения, у которых ещё не зафиксированы и причина, и влияние. */
  decisionsNeedingContext: Decision[];
  projectSummaries: ProjectSummary[];
  recentNotes: NoteStub[];
  todayEvents: import('./calendar').Occurrence[];
  reminders: import('./calendar').Occurrence[];
  propertyRechecks: {
    unit_code: string;
    verified_at: string | null;
    verified_by: string;
    next_check_at: string;
    verification_status: 'UNVERIFIED' | 'NEEDS_RECHECK';
  }[];
  settings: Record<string, string>;
}

export type AttentionLane = 'requires_you' | 'in_progress' | 'waiting';
export type AttentionSourceStatus = 'ok' | 'not_configured' | 'blocked' | 'unavailable' | 'pending';

export interface AttentionItem {
  key: string;
  source: 'jack_inbox' | 'control_plane_run' | 'task' | 'task_inbox' | 'decision' | 'waiting' | 'market' | 'oleg' | 'lead' | 'money' | 'tm30' | 'transfer' | 'guest_inbox';
  source_id: string;
  title: string;
  summary: string | null;
  status_label: string;
  actor_label: string | null;
  unit_code: string | null;
  booking_id: string | null;
  lead_id: string | null;
  assignee: string | null;
  due_at: string | null;
  href: string;
  action_label: string;
  /** Market Intelligence our-ask / median ratio when the recommendation payload includes it. */
  market_ratio?: number | null;
  /** Market Intelligence comparable count (N) when the recommendation payload includes it. */
  market_n?: number | null;
  source_state?: string;
  source_updated_at?: string | null;
  original_lane?: AttentionLane;
  original_assignee?: string | null;
  routing_owner?: string | null;
  review_kind?: 'finance';
  review_state?: 'jack' | 'delegated';
  triage_keys?: string[];
  triage_owner?: string | null;
  triage_owners?: Array<string | null>;
  duplicate_count?: number;
  from_own?: boolean;
}

export interface FinanceReviewMutation {
  ok: true;
  created: boolean;
  attention_key: string;
  state: 'jack' | 'delegated';
  assignee: {
    id: string;
    name: string;
    title: string;
    kind: 'core' | 'specialized';
  } | null;
}

export interface AttentionReadModel {
  generated_at: string;
  requires_you: AttentionItem[];
  in_progress: AttentionItem[];
  waiting: AttentionItem[];
  sources: Record<
    'jack_inbox' | 'control_plane_runs' | 'tasks' | 'waiting' | 'market' | 'oleg' | 'leads' | 'money' | 'tm30' | 'transfers',
    { status: AttentionSourceStatus; count: number }
  > & Partial<Record<'decisions' | 'task_inbox' | 'guest_inbox', { status: AttentionSourceStatus; count: number }>>;
}

export interface OlegSignal {
  id: string;
  label: string;
  kind: string;
  severity: 'critical' | 'review' | 'warning' | 'info';
  status: string;
  agent_name: string | null;
  booking_id: number | null;
  checkin: string | null;
  checkout: string | null;
  unit_code: string | null;
  oleg_url: string | null;
}

export interface OlegSignals {
  configured: boolean;
  ok: boolean;
  source_status: 'ok' | 'not_configured' | 'blocked' | 'unavailable';
  date: string;
  items: OlegSignal[];
  summary: {
    total_items: number;
    critical_items: number;
    overdue_items: number;
  };
  source_contract: {
    allowed: boolean;
    actual: string;
    required: string[];
  };
  error: string;
}

/** Итог идемпотентного переноса сигнала Oleg во «Входящие». */
export interface OlegSignalTaskResult {
  task: Pick<Task, 'id'>;
  created: boolean;
}

export interface OlegChatResponse {
  ok: boolean;
  caption: string | null;
  error: string;
}

export interface OlegRecommendResponse {
  ok: boolean;
  date: string;
  attention: {
    label: string;
    unit_code: string | null;
    severity: 'critical' | 'review' | 'warning' | 'info';
    kind: string;
  }[];
  money: {
    revenue_today: number;
    cash_in_today: number;
    expenses_today: number;
    owner_payable_today: number;
  };
  today: {
    checkins: number;
    checkouts: number;
  };
  overdue_count: number;
  error: string;
}

export interface OlegChatMessage {
  role: 'user' | 'oleg';
  text: string;
  created_at: number;
}

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';

export interface ControlPlaneApproval {
  approval_id: string;
  execution_id: string;
  capability_id: string;
  capability_version: number;
  phase: 'pre_dispatch' | 'provider_runtime';
  status: ApprovalStatus;
  scope: {
    action: string;
    target: string;
    protected_input_hash: string;
    risk_summary: string;
  };
  scope_hash: string;
  provider_approval_id?: string | null;
  requested_at: string;
  expires_at: string;
  decided_at?: string | null;
  decided_by?: { client_id: string; actor_id: string } | null;
  reason?: string | null;
  /** Enriched by the Jack OS UI from the linked execution; not persisted locally. */
  requested_by?: { client_id: string; actor_id: string };
}
